/**
 * Step picker (modal). Lists existing steps in the project that are not already
 * in the current orientation, so an author can REUSE a step instead of creating
 * a new one. Reuse is shared-content: the picked step keeps the same fields and
 * per-version scripts everywhere it appears (see the editor's save warning).
 * "Copy" adds an independent duplicate instead, for a near-identical stop.
 *
 * Orphan steps (usage 0 - not linked to any orientation) sort first so they stay
 * findable, and get an inline Delete so the step pool can be cleaned up here
 * rather than accumulating invisibly in the DB.
 */
import { useMemo, useState } from 'react';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '../Icon';

export type PickerCandidate = {
  stepId: string;
  name: string;
  /** Number of orientations already using this step (0 = orphan). */
  usage: number;
  /** Authored beats in the editable slot. */
  beats: number;
};

type Props = {
  candidates: PickerCandidate[];
  onPick: (stepId: string) => void;
  /** Add an independent copy of the step instead of the shared step. */
  onCopy: (stepId: string) => void;
  /** Permanently delete an orphan step (usage 0). */
  onDelete: (stepId: string) => void;
  onCancel: () => void;
};

export function StepPicker({ candidates, onPick, onCopy, onDelete, onCancel }: Props) {
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return candidates
      .filter((c) => !needle || c.name.toLowerCase().includes(needle))
      // Orphans (usage 0) first so they stay discoverable, then by name.
      .sort((a, b) => (a.usage === 0 ? 0 : 1) - (b.usage === 0 ? 0 : 1) || a.name.localeCompare(b.name));
  }, [candidates, q]);

  return (
    <div className="oe-modal-bd" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="oe-modal">
        <div className="oe-modal-head">
          <h2>Add an existing stop</h2>
          <button className="oe-btn ghost sm" aria-label="Close" onClick={onCancel}><Icon of={Icons.action.remove} size={14} /></button>
        </div>
        <div className="oe-modal-body">
          <div className="oe-field">
            <input
              className="oe-inp"
              placeholder="Search stops…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
          </div>
          {rows.length === 0 ? (
            <div className="oe-empty">
              <h4>{candidates.length === 0 ? 'No reusable stops' : 'No matches'}</h4>
              {candidates.length === 0
                ? 'Every stop in this project is already in this orientation.'
                : 'Try a different search.'}
            </div>
          ) : (
            <div className="oe-list" style={{ maxHeight: 360, overflow: 'auto' }}>
              {rows.map((c) => (
                <div key={c.stepId} className="oe-stop-item" style={{ display: 'flex', gap: 9 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="title">
                      {c.name}
                      <span className="oe-reuse-badge">{c.beats} beat{c.beats === 1 ? '' : 's'}</span>
                    </div>
                    <div className="meta">
                      {c.usage > 0 ? `used in ${c.usage} orientation${c.usage === 1 ? '' : 's'}` : 'unused'}
                    </div>
                  </div>
                  {c.usage === 0 && (
                    <button
                      className="oe-btn danger sm"
                      title="Delete this unused stop permanently"
                      onClick={() => onDelete(c.stepId)}
                    >Delete</button>
                  )}
                  <button
                    className="oe-btn sm"
                    title="Add an independent copy you can edit without affecting the original"
                    onClick={() => onCopy(c.stepId)}
                  >Copy</button>
                  <button
                    className="oe-btn primary sm"
                    title="Add the shared stop: edits apply everywhere it is used"
                    onClick={() => onPick(c.stepId)}
                  >Add</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="oe-modal-foot">
          <button className="oe-btn" onClick={onCancel}>Close</button>
        </div>
      </div>
    </div>
  );
}
